/**
 * The card front carries three, four or five pointers: never two, never a wall.
 *
 * USER 2026-09-13, holding two cards: "people are telling the information is too
 * overwhelming in one card ... there should be only 3-4 perfect points for the story,
 * there should be nothing else". And later the same day, holding a two-pointer card: "there
 * should never be 2 pointers, it should be minimum 3 and maximum 5. 2 looks very thin, people
 * should be interested to read and easy to read, not be disappointed."
 *
 * So: at most five, and when the body offers fewer than three the front borrows from Why it
 * matters, one sentence at a time, until it has three. Five short lines read as a glance;
 * five long ones read as an article, so the budget is measured in characters as well as
 * lines. Nothing is rewritten and the author's order is kept.
 */

/** The most the front will carry, whatever the source offers. */
export const FRONT_MAX_POINTS = 5;
/** Fewer than this reads as thin; the front borrows from Why it matters to reach it. */
export const FRONT_MIN_POINTS = 3;
/** Beyond three, pointers may share this many characters; past it the last folds into the dive. */
export const FRONT_CHAR_BUDGET = 320;
/** A borrowed sentence must be long enough to be a pointer and short enough to stay one. */
const BORROW_MIN = 24;
const BORROW_MAX = 150;

function clean(point: string): string {
  return point.replace(/\s+/g, " ").trim();
}

const SENTENCE_BREAK = /(?<=[.!?]["')\]]?)\s+(?=[A-Z0-9"'(])/;

function key(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/**
 * Pick the pointers the card front shows.
 * 1. The author's pointers, at most five.
 * 2. Fewer than three: borrow sentences from Why it matters (in order, no repeats) until three.
 * 3. Still fewer than three: return nothing, so the caller shows the body as prose rather
 *    than a two-line list.
 * 4. More than three: fold the last away while the list is over its character budget.
 */
export function frontPoints(points: readonly string[], whyItMatters?: string | null): string[] {
  const kept = points.map(clean).filter(Boolean).slice(0, FRONT_MAX_POINTS);
  if (kept.length < FRONT_MIN_POINTS && whyItMatters) {
    const have = new Set(kept.map(key));
    for (const sentence of clean(whyItMatters).split(SENTENCE_BREAK).map(clean)) {
      if (kept.length >= FRONT_MIN_POINTS) break;
      if (sentence.length < BORROW_MIN || sentence.length > BORROW_MAX) continue;
      const k = key(sentence);
      if (have.has(k)) continue;
      have.add(k);
      kept.push(sentence);
    }
  }
  if (kept.length < FRONT_MIN_POINTS) return [];
  while (kept.length > FRONT_MIN_POINTS && kept.reduce((sum, p) => sum + p.length, 0) > FRONT_CHAR_BUDGET) {
    kept.pop();
  }
  return kept;
}

/**
 * The card body as paragraphs for the dive's "The story" chapter, when the pipeline gave the
 * card no fuller story. Two sentences per paragraph; a very long sentence stands alone. The
 * front shows the pointers, so the dive is the only place the whole body is read.
 */
export function bodyParagraphs(body: string): string[] {
  const text = clean(body);
  if (!text) return [];
  const sentences = text.split(SENTENCE_BREAK).map(clean).filter(Boolean);
  const out: string[] = [];
  let cur: string[] = [];
  for (const s of sentences) {
    if (s.length > 200) {
      if (cur.length) out.push(cur.join(" "));
      out.push(s);
      cur = [];
      continue;
    }
    cur.push(s);
    if (cur.length === 2) {
      out.push(cur.join(" "));
      cur = [];
    }
  }
  if (cur.length) out.push(cur.join(" "));
  return out;
}
